// Pure helpers powering the KPI Overview panel.
//
// computeOverallCompliance: the canonical compliance definition, on the
// calendar-month axis. The maths itself lives in `monthCompliance.ts`, shared
// with the KPI compliance report so the two surfaces can never show a
// contract two different numbers: a result belongs to the month its reporting
// period CLOSES in, a KPI's month score is the mean of its own results that
// month (`green=1`, `amber=0.5`, `red=0` — locked compliance policy), and a
// month's compliance is the equal-weighted mean across the KPIs that
// reported. Amber is partial credit — a KPI inside its tolerance band
// contributes 50% rather than counting as a hard miss, matching the QITC
// traffic-light convention used in supplier scorecards.
// categoriseKpiByRag: bucket KPIs by their latest report's RAG. KPIs with no
// report are excluded from every bucket.
// normaliseSparklinePoints: produce 0..100-scaled x points for rendering
// with pure SVG (no Recharts dependency). Rejects non-numeric values.
//
// Range filtering rules:
//   * Compliance (30d / 90d / 12m / ytd) windows CALENDAR MONTHS ending at the
//     current month — 1, 3 and 12 months respectively, and January to the
//     current month for ytd. The window is anchored on the calendar rather
//     than on the data, so the tab's 12m headline covers exactly the months
//     the report's 12m view covers and the two agree figure for figure. A
//     month nobody has reported yet reads as no measurement, never as 0%.
//   * Sparklines and per-KPI averages keep the older "last N period keys
//     present in the data" window (see WINDOW_BY_FREQ), because they plot raw
//     measured values rather than compliance and have no month axis to sit
//     on. An optional `now` parameter makes the ytd prefix filter testable;
//     the year is read with `getUTCFullYear()` so local timezone drift can
//     never change the cutoff.

import {
  type Kpi,
  type KpiMeasurement,
  type KpiReport,
  type KpiValueType,
} from "../../types";
import {
  matchKpiFrequency,
  classifyBooleanKpiValue,
} from "./kpiValueParsers";
import {
  addMonths,
  computeMonthlyCompliance,
  monthOfDate,
  monthSpan,
  placeResultsOnMonths,
  type ComplianceKpi,
  type ComplianceResult,
} from "./monthCompliance";

export type PeriodRange = "30d" | "90d" | "12m" | "ytd";

export interface ComplianceSummary {
  /** The range average, rounded to one decimal. Null when no month in the
   *  window carries a result — a headline of 0% would state total failure
   *  where the truth is that nothing has been measured yet. */
  actual: number | null;
  /** One entry per month that carries a result, ascending. Months nobody
   *  reported in are absent rather than zeroed. */
  byPeriod: Array<{ period: string; value: number }>;
}

export interface RagCategoryCounts {
  onTrack: number;
  atRisk: number;
  failing: number;
}

export interface SparklinePoint {
  x: number;
  y: number;
}

type Frequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annually"
  | "unknown";

function normaliseFrequency(raw: string): Frequency {
  // Unrecognised frequencies stay in their own "unknown" bucket here so
  // windowing can treat them distinctly (period.ts instead defaults to
  // monthly because its date maths needs a concrete frequency).
  return matchKpiFrequency(raw) ?? "unknown";
}

// Number of periods to keep for (range, frequency). Uses coarse approximations
// — e.g. 30d on a daily KPI = ~30 points; on monthly = 1 period. Set to a
// large sentinel (Infinity) for "keep all" (ytd uses prefix filter instead).
const WINDOW_BY_FREQ: Record<
  Exclude<PeriodRange, "ytd">,
  Record<Frequency, number>
> = {
  "30d": {
    daily: 30,
    weekly: 4,
    monthly: 1,
    quarterly: 1,
    annually: 1,
    unknown: 1,
  },
  "90d": {
    daily: 90,
    weekly: 13,
    monthly: 3,
    quarterly: 1,
    annually: 1,
    unknown: 3,
  },
  "12m": {
    daily: 365,
    weekly: 52,
    monthly: 12,
    quarterly: 4,
    annually: 1,
    unknown: 12,
  },
};

function currentYearPrefix(now: Date = new Date()): string {
  return String(now.getUTCFullYear());
}

/** Calendar months a compliance range covers, ending at the current month. */
const RANGE_MONTH_COUNT: Record<Exclude<PeriodRange, "ytd">, number> = {
  "30d": 1,
  "90d": 3,
  "12m": 12,
};

/** The months a range covers, ascending. Anchored on the calendar rather than
 *  on the data so the window states a fixed span the reader can name — and so
 *  it matches the report's window for the same number of months. */
export function complianceWindow(
  range: PeriodRange,
  now: Date = new Date(),
): string[] {
  const end = monthOfDate(now);
  const start =
    range === "ytd"
      ? `${now.getUTCFullYear()}-01`
      : addMonths(end, -(RANGE_MONTH_COUNT[range] - 1));
  return monthSpan(start, end);
}

/** KPI definitions reduced to what the shared aggregation reads. */
function toComplianceKpis(kpis: Kpi[]): ComplianceKpi[] {
  return kpis.map((kpi) => ({
    kpiId: kpi.id,
    measurementFrequency: kpi.measurementFrequency,
  }));
}

/** Logged reports reduced to what the shared aggregation reads. A report's
 *  `updatedAt` decides which row wins when a period was logged twice, falling
 *  back to `createdAt` — the same preference `dedupeReportsByPeriod` applies. */
function toComplianceResults(reports: KpiReport[]): ComplianceResult[] {
  return reports.map((report) => ({
    kpiId: report.parentId,
    period: report.period,
    rating: report.ragRating,
    recordedAt: report.updatedAt ?? report.createdAt ?? null,
  }));
}

export function computeOverallCompliance(
  kpis: Kpi[],
  reports: KpiReport[],
  range: PeriodRange,
  now?: Date,
): ComplianceSummary {
  const months = complianceWindow(range, now);
  const compliance = computeMonthlyCompliance(
    toComplianceKpis(kpis),
    toComplianceResults(reports),
    months,
  );

  return {
    actual: compliance.rangeWeightedPct,
    byPeriod: compliance.months.flatMap((point) =>
      point.weightedPct === null
        ? []
        : [{ period: point.month, value: point.weightedPct }],
    ),
  };
}

// Summary-strip row 2. `hits` counts months where the weighted compliance
// >= target (default TARGET_COMPLIANCE_DEFAULT), out of the months that
// carry a result. Months come from computeOverallCompliance so the window is
// consistent across all strip copy.
export interface PeriodHitStats {
  hits: number;
  periods: number;
}

export const TARGET_COMPLIANCE_DEFAULT = 95;

export function computeCompliancePeriods(
  byPeriod: Array<{ period: string; value: number }>,
  target: number = TARGET_COMPLIANCE_DEFAULT,
): PeriodHitStats {
  const hits = byPeriod.filter((p) => p.value >= target).length;
  return { hits, periods: byPeriod.length };
}

// "Failing periods" card, on the same calendar-month axis as the headline. A
// month counts as failing if any of its KPIs was red in it; `kpiCount` =
// distinct KPIs that were red at least once; `inLastQuarter` = failing months
// among the last three entries of `byPeriod`.
//
// `byPeriod` carries only the months that actually reported, so those three
// entries are the last three REPORTED months, not the last three calendar
// months — on a contract with gaps they can reach further back than a
// quarter. It is still a truer reading than the old one, where three entries
// meant three weeks for a weekly KPI and three quarters for a quarterly one.
export interface FailingPeriodStats {
  failingPeriodCount: number;
  kpiCount: number;
  inLastQuarter: number;
}

export function computeFailingPeriodStats(
  kpis: Kpi[],
  reports: KpiReport[],
  byPeriod: Array<{ period: string; value: number }>,
): FailingPeriodStats {
  if (byPeriod.length === 0) {
    return { failingPeriodCount: 0, kpiCount: 0, inLastQuarter: 0 };
  }
  const rangeMonths = new Set(byPeriod.map((p) => p.period));
  const failingByMonth = new Map<string, Set<string>>();
  for (const placed of placeResultsOnMonths(
    toComplianceKpis(kpis),
    toComplianceResults(reports),
  )) {
    if (!rangeMonths.has(placed.month)) continue;
    if (placed.rating !== "red") continue;
    let set = failingByMonth.get(placed.month);
    if (!set) {
      set = new Set();
      failingByMonth.set(placed.month, set);
    }
    set.add(placed.kpiId);
  }
  const failingMonths = Array.from(failingByMonth.keys());
  const kpiIdsRed = new Set<string>();
  for (const set of failingByMonth.values()) {
    for (const id of set) kpiIdsRed.add(id);
  }
  const lastQuarter = new Set(byPeriod.slice(-3).map((p) => p.period));
  const inLastQuarter = failingMonths.filter((month) =>
    lastQuarter.has(month),
  ).length;
  return {
    failingPeriodCount: failingMonths.length,
    kpiCount: kpiIdsRed.size,
    inLastQuarter,
  };
}

// Per-KPI "Compliance {X}%" line on the Overview card. The same canonical
// aggregation over a single KPI, so a card and the headline are computed the
// same way. Returns null when the KPI reported in no month of the range, so
// the caller hides the segment rather than showing a manufactured 0%.
export function computeKpiCompliance(
  kpiId: string,
  reports: KpiReport[],
  kpis: Kpi[],
  range: PeriodRange,
  now?: Date,
): number | null {
  const kpi = kpis.find((k) => k.id === kpiId);
  if (!kpi) return null;
  return computeOverallCompliance([kpi], reports, range, now).actual;
}

// KPI card "Xm avg {N}" secondary line. Mean of numeric
// measurement values over the range-filtered window. Null when no
// numeric measurements fall in range.
//
// Callers pass the KPI's `valueType` so boolean KPIs
// short-circuit to null unconditionally. Met/Not-met values have no
// numeric average; the card + drawer suppress the "avg" segment in
// that branch.
export function computeKpiAverage(
  measurements: KpiMeasurement[],
  frequency: string,
  range: PeriodRange,
  now?: Date,
  valueType?: KpiValueType | null,
): number | null {
  if (valueType === "boolean") return null;
  const points = normaliseSparklinePoints(
    measurements,
    frequency,
    range,
    now,
  );
  if (points.length === 0) return null;
  const sum = points.reduce((acc, p) => acc + p.y, 0);
  return Math.round(sum / points.length);
}

export function categoriseKpiByRag(
  kpis: Kpi[],
  latestByKpiId: Map<string, KpiReport>,
): RagCategoryCounts {
  const counts: RagCategoryCounts = { onTrack: 0, atRisk: 0, failing: 0 };
  for (const kpi of kpis) {
    const r = latestByKpiId.get(kpi.id);
    if (!r) continue;
    if (r.ragRating === "green") counts.onTrack += 1;
    else if (r.ragRating === "amber") counts.atRisk += 1;
    else if (r.ragRating === "red") counts.failing += 1;
  }
  return counts;
}

function parseNumeric(raw: string): number | null {
  if (!raw) return null;
  const cleaned = raw.trim().replace(/%$/u, "").trim();
  if (cleaned.length === 0) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

// Dedupe a list of measurements to one-per-period, keeping the row with
// the highest `date` (tie-break: highest `updatedAt` then `id`). Same
// rule history table + `latestMeasurementByKpi` already use — pulled
// out here so charts, sparklines, and stats share a single source of
// truth when storage contains duplicate (parentId, period) rows.
export function dedupeMeasurementsByPeriod(
  measurements: KpiMeasurement[],
): KpiMeasurement[] {
  const latestPerPeriod = new Map<string, KpiMeasurement>();
  for (const m of measurements) {
    const existing = latestPerPeriod.get(m.period);
    if (!existing) {
      latestPerPeriod.set(m.period, m);
      continue;
    }
    const byDate = m.date.localeCompare(existing.date);
    if (byDate > 0) {
      latestPerPeriod.set(m.period, m);
      continue;
    }
    if (byDate === 0) {
      const aUpdated = m.updatedAt ?? "";
      const bUpdated = existing.updatedAt ?? "";
      if (aUpdated.localeCompare(bUpdated) > 0) {
        latestPerPeriod.set(m.period, m);
      } else if (aUpdated === bUpdated && m.id.localeCompare(existing.id) > 0) {
        latestPerPeriod.set(m.period, m);
      }
    }
  }
  return Array.from(latestPerPeriod.values());
}

// Same dedupe rule for reports — there's no `date` field on a report so
// we order by `updatedAt` (fall back to `createdAt`, then `id`).
export function dedupeReportsByPeriod(reports: KpiReport[]): KpiReport[] {
  const latestPerPeriod = new Map<string, KpiReport>();
  for (const r of reports) {
    const existing = latestPerPeriod.get(r.period);
    if (!existing) {
      latestPerPeriod.set(r.period, r);
      continue;
    }
    const aKey = r.updatedAt ?? r.createdAt ?? "";
    const bKey = existing.updatedAt ?? existing.createdAt ?? "";
    const byUpdated = aKey.localeCompare(bKey);
    if (byUpdated > 0) {
      latestPerPeriod.set(r.period, r);
    } else if (byUpdated === 0 && r.id.localeCompare(existing.id) > 0) {
      latestPerPeriod.set(r.period, r);
    }
  }
  return Array.from(latestPerPeriod.values());
}

export function normaliseSparklinePoints(
  measurements: KpiMeasurement[],
  frequency: string,
  range: PeriodRange,
  now?: Date,
  valueType?: KpiValueType | null,
): SparklinePoint[] {
  if (measurements.length === 0) return [];

  let ordered = dedupeMeasurementsByPeriod(measurements).sort((a, b) =>
    a.period.localeCompare(b.period),
  );

  // Range filter — the period-count window described in the header, not the
  // compliance month window: these are raw measured values, not compliance.
  if (range === "ytd") {
    const prefix = currentYearPrefix(now);
    ordered = ordered.filter((m) => m.period.startsWith(prefix));
  } else {
    const freq = normaliseFrequency(frequency);
    const keep = WINDOW_BY_FREQ[range][freq];
    if (Number.isFinite(keep) && ordered.length > keep) {
      ordered = ordered.slice(-keep);
    }
  }

  const isBoolean = valueType === "boolean";

  const values: Array<{ period: string; y: number }> = [];
  for (const m of ordered) {
    let y: number | null;
    if (isBoolean) {
      const cls = classifyBooleanKpiValue(m.value);
      if (cls === "met") y = 100;
      else if (cls === "not-met") y = 0;
      else y = null;
    } else {
      y = parseNumeric(m.value);
    }
    if (y === null) continue;
    values.push({ period: m.period, y });
  }

  if (values.length === 0) return [];
  if (values.length === 1) return [{ x: 0, y: values[0].y }];

  const denom = values.length - 1;
  return values.map((v, i) => ({
    x: Math.round((i / denom) * 100),
    y: v.y,
  }));
}
